/**
 * Task #262 — Time Off dialog.
 *
 * Members see a Request tab with a MonthCalendar (drag to select range)
 * plus their own request list with a sub-range cancellation popover.
 * Admins additionally see a Review tab with a pending queue (with
 * optional decision note), a per-user yearly usage table, and a
 * team-wide overlay calendar where each selected user is painted in a
 * deterministic colour so absences cluster visibly.
 *
 * All NEW UI strings are English (project convention).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/auth-context";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { MonthCalendar, type MonthCalendarRange, type RenderCellArgs, type MonthCalendarEvent } from "@/components/calendar/MonthCalendar";
import { formatInTimeZone } from "date-fns-tz";
import { CalendarOff, Check, X, Trash2, Scissors, ChevronLeft, ChevronRight, Building2, Plus } from "lucide-react";
import { cn } from "@/lib/utils";

interface TimeOffRequest {
  id: number;
  userId: number;
  startDate: string;
  endDate: string;
  type: "vacation" | "sick" | "other";
  status: "pending" | "approved" | "rejected" | "cancellation_pending" | "cancelled" | "superseded";
  parentRequestId: number | null;
  reason: string | null;
  decisionNote: string | null;
  decidedBy: number | null;
  decidedAt: string | null;
  createdAt: string;
}

interface HolidayInfo { date: string; name: string }
interface CompanyHoliday { id: number; startDate: string; endDate: string; name: string; createdBy: number | null; createdAt: string }
interface TimeOffUser { id: number; firstName: string | null; lastName: string | null; username: string; timeOffAllowance?: number | null; colorCode?: string | null }
interface UsageRow { userId: number; firstName: string | null; lastName: string | null; username: string; approvedDays: number; pendingDays: number; sickApprovedDays: number; sickPendingDays: number; companyDays: number; allowance: number | null; remaining: number | null }

interface CalEvt extends MonthCalendarEvent {
  date: string;
  kind: "holiday" | "company" | "request";
  request?: TimeOffRequest;
  holidayName?: string;
  companyHolidayName?: string;
  userColor?: string;
}

interface TimeOffDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const TYPE_COLOR: Record<string, string> = {
  vacation: "bg-emerald-700/70 text-emerald-100",
  sick: "bg-orange-700/70 text-orange-100",
  other: "bg-purple-700/70 text-purple-100",
};
const STATUS_LABEL: Record<string, string> = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
  cancellation_pending: "Cancellation pending",
  cancelled: "Cancelled",
  superseded: "Superseded",
};
const STATUS_OPTIONS = ["pending", "approved", "rejected", "cancellation_pending"] as const;

// Deterministic per-user colour palette for the admin overlay.
const USER_PALETTE = [
  "#ef4444", "#f97316", "#eab308", "#22c55e", "#06b6d4",
  "#3b82f6", "#a855f7", "#ec4899", "#14b8a6", "#84cc16",
];
function userColor(id: number) { return USER_PALETTE[id % USER_PALETTE.length]; }

function authedFetch(url: string) {
  return fetch(url, { credentials: "include" }).then((r) => r.json());
}

function expandRange(startYmd: string, endYmd: string): string[] {
  const out: string[] = [];
  let d = startYmd;
  while (d <= endYmd) {
    out.push(d);
    const next = new Date(d + "T12:00:00Z");
    next.setUTCDate(next.getUTCDate() + 1);
    d = next.toISOString().slice(0, 10);
  }
  return out;
}

/** Hamburg-style weekday check (Sun=0..Sat=6 in UTC; safe for our YYYY-MM-DD). */
function isWeekendYmd(ymd: string): boolean {
  const d = new Date(`${ymd}T12:00:00Z`).getUTCDay();
  return d === 0 || d === 6;
}

/** Format a YYYY-MM-DD inclusive range. Single days collapse to one date. */
function formatRange(startYmd: string, endYmd: string): string {
  return startYmd === endYmd ? startYmd : `${startYmd} → ${endYmd}`;
}

export function TimeOffDialog({ open, onOpenChange }: TimeOffDialogProps) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState<string>("request");
  // Berlin-local "today" anchors the initial year/month — see MonthCalendar
  // for the project-wide Europe/Berlin date-math rule.
  const [year, setYear] = useState(() => Number(formatInTimeZone(new Date(), "Europe/Berlin", "yyyy")));
  const [month, setMonth] = useState(() => Number(formatInTimeZone(new Date(), "Europe/Berlin", "M")));
  const onMonthChange = useCallback((y: number, m: number) => { setYear(y); setMonth(m); }, []);
  // List year chevrons: keep the calendar in sync by holding the current
  // month and only shifting the year — the calendar's own < / > buttons
  // already feed back through onMonthChange.
  const shiftYear = useCallback((delta: number) => {
    setYear((y) => y + delta);
  }, []);

  // Mark notifications as read when the dialog opens — fire once per open.
  useEffect(() => {
    if (!open) return;
    apiRequest("PATCH", "/api/time-off/notifications/read-all", {})
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ["/api/time-off/badge"] });
        queryClient.invalidateQueries({ queryKey: ["/api/time-off/notifications"] });
      })
      .catch(() => {});
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl bg-gray-900 border-gray-700 text-gray-100 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <CalendarOff className="w-4 h-4" /> Time Off
          </DialogTitle>
        </DialogHeader>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-gray-800">
            <TabsTrigger value="request">Request</TabsTrigger>
            {isAdmin && <TabsTrigger value="review">Review</TabsTrigger>}
          </TabsList>
          <TabsContent value="request" className="mt-2">
            <RequestTab year={year} month={month} onMonthChange={onMonthChange} shiftYear={shiftYear} />
          </TabsContent>
          {isAdmin && (
            <TabsContent value="review" className="mt-2 space-y-4">
              <ReviewTab year={year} setYear={setYear} month={month} onMonthChange={onMonthChange} />
            </TabsContent>
          )}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Request tab (member view)
// ---------------------------------------------------------------------------
function RequestTab({ year, month, onMonthChange, shiftYear }: { year: number; month: number; onMonthChange: (y: number, m: number) => void; shiftYear: (delta: number) => void }) {
  const { toast } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [range, setRange] = useState<MonthCalendarRange | null>(null);
  const [type, setType] = useState<"vacation" | "sick" | "other">("vacation");
  const [reason, setReason] = useState("");
  // Admin-only: create the request on behalf of another team member.
  // "me" sentinel = the admin's own request (default, member behaviour).
  const [forUserId, setForUserId] = useState<string>("me");
  const { data: teamUsers = [] } = useQuery<TimeOffUser[]>({
    queryKey: ["/api/time-off/users"],
    enabled: isAdmin,
  });

  const { data: holidays = [] } = useQuery<HolidayInfo[]>({
    queryKey: ["/api/time-off/holidays", year],
    queryFn: () => authedFetch(`/api/time-off/holidays?year=${year}&includeAdjacent=1`),
  });
  const { data: companyHolidays = [] } = useQuery<CompanyHoliday[]>({
    queryKey: ["/api/time-off/company-holidays", year],
    queryFn: () => authedFetch(`/api/time-off/company-holidays?year=${year}`),
  });
  const { data: mine = [] } = useQuery<TimeOffRequest[]>({ queryKey: ["/api/time-off/mine"] });
  const { data: myUsage } = useQuery<{ year: number; row: UsageRow | null }>({
    queryKey: ["/api/time-off/usage/me", { year }],
  });

  // Filter own requests so the list mirrors the chosen year (overlap test).
  const mineThisYear = useMemo(() => {
    const ys = `${year}-01-01`;
    const ye = `${year}-12-31`;
    return mine.filter((r) => r.startDate <= ye && r.endDate >= ys);
  }, [mine, year]);
  const { data: businessDayResp } = useQuery<{ businessDays: number }>({
    queryKey: ["/api/time-off/business-days", range?.start, range?.end],
    queryFn: () => authedFetch(`/api/time-off/business-days?start=${range!.start}&end=${range!.end}`),
    enabled: !!range,
  });

  const targetUser = forUserId !== "me" ? teamUsers.find((u) => String(u.id) === forUserId) : undefined;
  const targetName = targetUser
    ? [targetUser.firstName, targetUser.lastName].filter(Boolean).join(" ") || targetUser.username
    : null;

  const createMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/time-off", {
      startDate: range!.start, endDate: range!.end, type, reason: reason.trim() || null,
      ...(isAdmin && forUserId !== "me" ? { userId: Number(forUserId) } : {}),
    }),
    onSuccess: () => {
      toast({
        title: "Request submitted",
        description: targetName ? `Created on behalf of ${targetName}. Approve it in the Review tab.` : undefined,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/time-off/mine"] });
      queryClient.invalidateQueries({ queryKey: ["/api/time-off/badge"] });
      if (isAdmin) {
        queryClient.invalidateQueries({ queryKey: ["/api/time-off/all"] });
        queryClient.invalidateQueries({ queryKey: ["/api/time-off/usage"] });
      }
      setRange(null); setReason(""); setForUserId("me");
    },
    onError: (err: any) => {
      // apiRequest throws Error(`${status}: ${body}`) — try to extract the
      // server's human-readable `message` (falls back to a sensible default
      // when the body isn't JSON or no message is set).
      const raw = String(err?.message ?? "");
      const m = raw.match(/^(\d+):\s*(.*)$/s);
      const status = m ? Number(m[1]) : 0;
      let description = "Please check the dates and try again.";
      try {
        if (m) {
          const body = JSON.parse(m[2]);
          if (body?.error === "overlap") {
            description = targetName
              ? `This range overlaps one of ${targetName}'s existing time-off requests.`
              : "This range overlaps one of your existing time-off requests. Pick different dates or cancel the existing request first.";
          } else if (body?.message) {
            description = body.message;
          }
        }
      } catch {
        if (status === 409) description = "This range overlaps one of your existing time-off requests.";
        else if (status === 400) description = "The selected range is invalid. Please pick a valid start and end date.";
      }
      toast({ title: "Could not submit request", description, variant: "destructive" });
    },
  });

  // Set of all YYYY-MM-DD covered by any company-holiday range — used
  // both to paint amber cells and to dim user-request badges on days
  // that would not consume the user's allowance.
  const companyDaySet = useMemo(() => {
    const s = new Set<string>();
    for (const h of companyHolidays) for (const d of expandRange(h.startDate, h.endDate)) s.add(d);
    return s;
  }, [companyHolidays]);
  const holidaySet = useMemo(() => new Set(holidays.map((h) => h.date)), [holidays]);

  const events: CalEvt[] = useMemo(() => {
    const out: CalEvt[] = [];
    for (const h of holidays) {
      out.push({ date: h.date, kind: "holiday", holidayName: h.name, color: "#f87171", label: h.name });
    }
    for (const h of companyHolidays) {
      for (const d of expandRange(h.startDate, h.endDate)) {
        out.push({ date: d, kind: "company", companyHolidayName: h.name, color: "#34d399", label: `Company: ${h.name}` });
      }
    }
    for (const r of mine) {
      if (["rejected", "cancelled", "superseded"].includes(r.status)) continue;
      for (const d of expandRange(r.startDate, r.endDate)) {
        out.push({ date: d, kind: "request", request: r, color: r.type === "vacation" ? "#10b981" : r.type === "sick" ? "#f97316" : "#a855f7", label: `${r.type} — ${STATUS_LABEL[r.status]}` });
      }
    }
    return out;
  }, [holidays, companyHolidays, mine]);

  const renderCell = useCallback((args: RenderCellArgs<CalEvt>) => {
    const holiday = args.holiday;
    const company = args.eventsOnDay.find((e) => e.kind === "company");
    const reqs = args.eventsOnDay.filter((e) => e.kind === "request");
    return (
      <div className={cn(
        "flex flex-col h-full",
        company && (args.isWeekend || !!holiday ? "bg-emerald-500/5 rounded-sm -m-0.5 p-0.5" : "bg-emerald-500/10 rounded-sm -m-0.5 p-0.5"),
      )}>
        <div className="flex items-start justify-between gap-1">
          <span className={cn("text-[10px] leading-none", args.isInCurrentMonth ? "text-gray-200" : "text-gray-500", holiday && "text-rose-300 font-medium", company && "text-emerald-300 font-medium")}>
            {Number(args.date.slice(8, 10))}
          </span>
          {holiday && (
            <span className="text-[8px] text-rose-300/80 truncate max-w-[60%]" title={holiday.holidayName}>{holiday.holidayName}</span>
          )}
          {!holiday && company && (
            <span className={cn("text-[8px] truncate max-w-[60%]", args.isWeekend ? "text-emerald-300/40" : "text-emerald-300/90")} title={`Company holiday: ${company.companyHolidayName}`}>{company.companyHolidayName}</span>
          )}
        </div>
        <div className="flex flex-col gap-0.5 mt-0.5">
          {reqs.slice(0, 2).map((e, i) => {
            const pending = e.request!.status === "pending" || e.request!.status === "cancellation_pending";
            // Day does not consume allowance if it's a weekend, public
            // holiday, or company holiday — render the request badge
            // at 20% opacity with a "(0)" tag so the user can SEE that
            // this day in the range is not being counted.
            const noCharge = args.isWeekend || !!holiday || !!company;
            const tooltip = noCharge
              ? `${e.request!.type} — does not count (${holiday ? "public holiday" : company ? "company holiday" : "weekend"})`
              : pending
                ? `${e.request!.type} — Pending approval (${STATUS_LABEL[e.request!.status]})`
                : `${e.request!.type} (${STATUS_LABEL[e.request!.status]})`;
            return (
              <span
                key={i}
                className={cn(
                  "text-[8px] rounded px-1 truncate border",
                  pending
                    ? "bg-gray-800/40 text-gray-300 italic border-dashed border-amber-400/70 bg-[repeating-linear-gradient(45deg,rgba(255,255,255,0.04)_0_4px,transparent_4px_8px)]"
                    : cn(TYPE_COLOR[e.request!.type], "border-transparent"),
                )}
                style={noCharge ? { opacity: 0.2 } : undefined}
                title={tooltip}
              >
                {e.request!.type}{pending && " …"}
              </span>
            );
          })}
        </div>
      </div>
    );
  }, []);

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-2">
          <MonthCalendar
            year={year} month={month} events={events}
            selectedRange={range} onRangeChange={setRange}
            onMonthChange={onMonthChange} renderCell={renderCell as any}
          />
          <p className="text-[11px] text-gray-400 mt-1">Click or drag to select a date range. Hamburg public holidays are highlighted in red.</p>
        </div>
        <div className="space-y-2">
          {isAdmin && (
            <div>
              <Label className="text-xs text-gray-300">Request for</Label>
              <Select value={forUserId} onValueChange={setForUserId}>
                <SelectTrigger className="bg-gray-800 border-gray-700 h-8 text-sm" data-testid="select-time-off-request-for"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700">
                  <SelectItem value="me">Myself</SelectItem>
                  {teamUsers.filter((u) => u.id !== user?.id).map((u) => (
                    <SelectItem key={u.id} value={String(u.id)}>
                      {[u.firstName, u.lastName].filter(Boolean).join(" ") || u.username}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs text-gray-300">Type</Label>
            <Select value={type} onValueChange={(v) => setType(v as any)}>
              <SelectTrigger className="bg-gray-800 border-gray-700 h-8 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-700">
                <SelectItem value="vacation">Vacation</SelectItem>
                <SelectItem value="sick">Sick</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-gray-300">Range</Label>
            <div className="flex items-center gap-2 text-sm text-gray-100">
              {range ? (
                <>
                  <Input value={range.start} readOnly className="h-7 bg-gray-800 border-gray-700 text-xs" />
                  <span className="text-gray-500">→</span>
                  <Input value={range.end} readOnly className="h-7 bg-gray-800 border-gray-700 text-xs" />
                </>
              ) : <span className="text-xs text-gray-500">No range selected</span>}
            </div>
            {range && <p className="text-[11px] text-gray-400 mt-1">{businessDayResp?.businessDays ?? "…"} business days (Hamburg)</p>}
          </div>
          <div>
            <Label className="text-xs text-gray-300">Reason (optional)</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, 150))}
              maxLength={150}
              rows={3}
              className="bg-gray-800 border-gray-700 text-sm"
            />
            <p className="text-[10px] text-gray-500 text-right mt-0.5">{reason.length}/150</p>
          </div>
          <Button disabled={!range || createMutation.isPending} onClick={() => createMutation.mutate()} className="w-full h-8" data-testid="button-submit-time-off">
            {targetName ? `Submit for ${targetName}` : "Submit request"}
          </Button>
        </div>
      </div>
      {myUsage?.row && (
        <div className="mt-4 rounded border border-gray-700 bg-gray-800/40 px-3 py-2 text-xs">
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-gray-300">
            <span className="font-medium text-gray-200">My {myUsage.year}</span>
            <span><span className="tabular-nums text-gray-100">{myUsage.row.approvedDays}</span> approved</span>
            <span><span className="tabular-nums text-gray-100">{myUsage.row.pendingDays}</span> pending</span>
            <span><span className="tabular-nums text-gray-100">{myUsage.row.allowance ?? "—"}</span> allowance</span>
            <span>
              <span className={cn(
                "tabular-nums",
                myUsage.row.remaining != null && myUsage.row.remaining < 0 ? "text-rose-300" : "text-gray-100",
              )}>{myUsage.row.remaining ?? "—"}</span> remaining
            </span>
          </div>
        </div>
      )}
      <div className="mt-4">
        <div className="flex items-center gap-1 mb-1">
          <h3 className="text-xs font-medium text-gray-300">My off days</h3>
          <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => shiftYear(-1)} title="Previous year">
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <span className="text-xs text-gray-200 tabular-nums w-10 text-center">{year}</span>
          <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => shiftYear(1)} title="Next year">
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
        <div className="rounded border border-gray-700 divide-y divide-gray-800">
          {mineThisYear.length === 0 && companyHolidays.length === 0 && (
            <div className="p-3 text-xs text-gray-500">No off days in {year}.</div>
          )}
          {mineThisYear.map((r) => <MyRequestRow key={r.id} request={r} companyDays={companyDaySet} holidayDays={holidaySet} />)}
          {companyHolidays.map((h) => <CompanyHolidayRow key={`ch-${h.id}`} holiday={h} holidayDays={holidaySet} />)}
        </div>
      </div>
    </div>
  );
}

/** Read-only row for an admin-set company holiday range inside the
 *  user's off-days list. Same compact layout as MyRequestRow but no
 *  actions. Shows the inclusive range plus the count of business days
 *  that actually consume allowance. */
function CompanyHolidayRow({ holiday: h, holidayDays }: { holiday: CompanyHoliday; holidayDays: Set<string> }) {
  const days = expandRange(h.startDate, h.endDate);
  const businessDays = days.filter((d) => !isWeekendYmd(d) && !holidayDays.has(d)).length;
  return (
    <div className="flex items-center gap-2 px-2 py-1 text-xs min-w-0">
      <Badge className="px-1 py-0 h-4 shrink-0 bg-emerald-700/70 text-emerald-100 gap-1">
        <Building2 className="w-3 h-3" />company
      </Badge>
      <span className="text-gray-200 shrink-0 whitespace-nowrap">{formatRange(h.startDate, h.endDate)}</span>
      <Badge variant="outline" className="px-1 py-0 h-4 shrink-0 border-gray-700 text-gray-400" title="Business days that consume allowance (weekends and public holidays are excluded)">
        {businessDays}d
      </Badge>
      <span className="text-gray-400 truncate min-w-0" title={h.name}>— {h.name}</span>
    </div>
  );
}

/** Small inline reason chip that opens a popover with the full text on click. */
function ReasonChip({ reason }: { reason: string }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="text-gray-500 hover:text-gray-200 truncate max-w-[160px] text-left text-xs shrink-0 underline decoration-dotted underline-offset-2"
          title="Click to view full reason"
        >
          — {reason}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 bg-gray-800 border-gray-700 text-gray-100 text-xs whitespace-pre-wrap break-words">
        {reason}
      </PopoverContent>
    </Popover>
  );
}

function MyRequestRow({ request: r, companyDays, holidayDays }: { request: TimeOffRequest; companyDays: Set<string>; holidayDays: Set<string> }) {
  // Count only days that ACTUALLY consume allowance — weekends,
  // public holidays, and company holidays are skipped.
  const charged = useMemo(() => {
    let n = 0;
    for (const d of expandRange(r.startDate, r.endDate)) {
      if (isWeekendYmd(d)) continue;
      if (holidayDays.has(d)) continue;
      if (companyDays.has(d)) continue;
      n++;
    }
    return n;
  }, [r.startDate, r.endDate, companyDays, holidayDays]);
  const { toast } = useToast();
  const [subOpen, setSubOpen] = useState(false);
  const [subStart, setSubStart] = useState(r.startDate);
  const [subEnd, setSubEnd] = useState(r.endDate);

  const cancelFull = useMutation({
    mutationFn: () => apiRequest("POST", `/api/time-off/${r.id}/cancel`, {}),
    onSuccess: () => {
      toast({ title: "Cancellation submitted" });
      queryClient.invalidateQueries({ queryKey: ["/api/time-off/mine"] });
    },
  });
  const cancelSub = useMutation({
    mutationFn: () => apiRequest("POST", `/api/time-off/${r.id}/cancel`, { rangeStart: subStart, rangeEnd: subEnd }),
    onSuccess: () => {
      toast({ title: "Partial cancellation submitted" });
      queryClient.invalidateQueries({ queryKey: ["/api/time-off/mine"] });
      setSubOpen(false);
    },
  });

  return (
    <div className="flex items-center gap-2 px-2 py-1 text-xs min-w-0">
      <Badge className={cn("px-1 py-0 h-4 shrink-0", TYPE_COLOR[r.type])}>{r.type}</Badge>
      <span className="text-gray-200 shrink-0 whitespace-nowrap">{r.startDate} → {r.endDate}</span>
      <Badge variant="outline" className="px-1 py-0 h-4 shrink-0 border-gray-600 text-gray-300">{STATUS_LABEL[r.status]}</Badge>
      <Badge variant="outline" className="px-1 py-0 h-4 shrink-0 border-gray-700 text-gray-400" title="Business days that consume allowance (weekends, public holidays, and company holidays are excluded)">
        {charged}d
      </Badge>
      {r.reason && <ReasonChip reason={r.reason} />}
      <div className="ml-auto shrink-0 flex items-center gap-1">
        {r.status === "approved" && r.startDate !== r.endDate && (
          <Popover open={subOpen} onOpenChange={setSubOpen}>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-[11px] text-gray-300 hover:text-amber-300">
                <Scissors className="w-3 h-3 mr-1" />Partial cancel
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 bg-gray-800 border-gray-700 text-gray-100">
              <div className="space-y-2">
                <p className="text-xs text-gray-300">Cancel only a portion of {r.startDate} → {r.endDate}</p>
                <div className="flex items-center gap-2">
                  <Input type="date" value={subStart} min={r.startDate} max={r.endDate}
                    onChange={(e) => setSubStart(e.target.value)} className="h-7 bg-gray-900 border-gray-700 text-xs" />
                  <span className="text-gray-500">→</span>
                  <Input type="date" value={subEnd} min={r.startDate} max={r.endDate}
                    onChange={(e) => setSubEnd(e.target.value)} className="h-7 bg-gray-900 border-gray-700 text-xs" />
                </div>
                <Button size="sm" className="w-full h-7"
                  disabled={cancelSub.isPending || subEnd < subStart}
                  onClick={() => cancelSub.mutate()}>
                  Submit partial cancellation
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        )}
        {(r.status === "pending" || r.status === "approved") && (
          <Button variant="ghost" size="sm" className="h-6 px-2 text-[11px] text-gray-300 hover:text-red-300" onClick={() => cancelFull.mutate()}>
            <Trash2 className="w-3 h-3 mr-1" />{r.status === "pending" ? "Cancel" : "Request cancel"}
          </Button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Review tab (admin)
// ---------------------------------------------------------------------------
function ReviewTab({ year, setYear, month, onMonthChange }: { year: number; setYear: (y: number) => void; month: number; onMonthChange: (y: number, m: number) => void }) {
  const { toast } = useToast();
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>(["pending", "approved", "cancellation_pending"]);
  const [decisionNotes, setDecisionNotes] = useState<Record<number, string>>({});

  const { data: teamUsers = [] } = useQuery<TimeOffUser[]>({ queryKey: ["/api/time-off/users"] });
  const { data: holidays = [] } = useQuery<HolidayInfo[]>({
    queryKey: ["/api/time-off/holidays", year],
    queryFn: () => authedFetch(`/api/time-off/holidays?year=${year}&includeAdjacent=1`),
  });
  const { data: companyHolidays = [] } = useQuery<CompanyHoliday[]>({
    queryKey: ["/api/time-off/company-holidays", year],
    queryFn: () => authedFetch(`/api/time-off/company-holidays?year=${year}`),
  });
  const [newCompanyStart, setNewCompanyStart] = useState("");
  const [newCompanyEnd, setNewCompanyEnd] = useState("");
  const [newCompanyName, setNewCompanyName] = useState("");
  const addCompanyHoliday = useMutation({
    mutationFn: () => apiRequest("POST", "/api/time-off/company-holidays", {
      startDate: newCompanyStart,
      endDate: newCompanyEnd || newCompanyStart,
      name: newCompanyName.trim(),
    }),
    onSuccess: () => {
      toast({ title: "Company holiday added" });
      setNewCompanyStart(""); setNewCompanyEnd(""); setNewCompanyName("");
      queryClient.invalidateQueries({ queryKey: ["/api/time-off/company-holidays", year] });
      queryClient.invalidateQueries({ queryKey: ["/api/time-off/usage", year] });
    },
    onError: (e: any) => {
      const msg = e?.message?.includes("invalid_range") ? "End date must be after start date" : "Could not add holiday";
      toast({ title: msg, variant: "destructive" });
    },
  });
  const deleteCompanyHoliday = useMutation({
    mutationFn: (id: number) => apiRequest("DELETE", `/api/time-off/company-holidays/${id}`, {}),
    onSuccess: () => {
      toast({ title: "Company holiday removed" });
      queryClient.invalidateQueries({ queryKey: ["/api/time-off/company-holidays", year] });
      queryClient.invalidateQueries({ queryKey: ["/api/time-off/usage", year] });
    },
  });
  const { data: allRequests = [] } = useQuery<TimeOffRequest[]>({
    queryKey: ["/api/time-off/all", statusFilter.join(","), selectedUserIds.join(",")],
    queryFn: () => {
      const params = new URLSearchParams();
      if (statusFilter.length > 0) params.set("statuses", statusFilter.join(","));
      if (selectedUserIds.length > 0) params.set("userIds", selectedUserIds.join(","));
      return authedFetch(`/api/time-off/all?${params.toString()}`);
    },
  });
  const { data: usage } = useQuery<{ year: number; rows: UsageRow[] }>({
    queryKey: ["/api/time-off/usage", year],
    queryFn: () => authedFetch(`/api/time-off/usage?year=${year}`),
  });

  const userMap = useMemo(() => {
    const m = new Map<number, string>();
    for (const u of teamUsers) m.set(u.id, u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.username);
    return m;
  }, [teamUsers]);

  // User colors: the custom per-user color from Settings (users.colorCode,
  // same as the artist badges) wins; the hash palette is only the fallback
  // for users without a custom color.
  const customColorMap = useMemo(() => {
    const m = new Map<number, string>();
    for (const u of teamUsers) if (u.colorCode) m.set(u.id, u.colorCode);
    return m;
  }, [teamUsers]);
  const colorFor = useCallback(
    (id: number) => customColorMap.get(id) ?? userColor(id),
    [customColorMap],
  );

  const pendingQueue = useMemo(
    () => allRequests.filter((r) => r.status === "pending" || r.status === "cancellation_pending"),
    [allRequests],
  );

  // Build the overlay calendar: holidays + per-user approved/pending ranges
  // filtered by selectedUserIds (or every team user if none selected).
  const overlayEvents: CalEvt[] = useMemo(() => {
    const out: CalEvt[] = [];
    for (const h of holidays) out.push({ date: h.date, kind: "holiday", holidayName: h.name, color: "#f87171", label: h.name });
    for (const h of companyHolidays) {
      for (const d of expandRange(h.startDate, h.endDate)) {
        out.push({ date: d, kind: "company", companyHolidayName: h.name, color: "#34d399", label: `Company: ${h.name}` });
      }
    }
    const userFilter = selectedUserIds.length > 0 ? new Set(selectedUserIds) : null;
    for (const r of allRequests) {
      if (["rejected", "cancelled", "superseded"].includes(r.status)) continue;
      if (userFilter && !userFilter.has(r.userId)) continue;
      const col = colorFor(r.userId);
      for (const d of expandRange(r.startDate, r.endDate)) {
        out.push({ date: d, kind: "request", request: r, userColor: col, color: col, label: `${userMap.get(r.userId) ?? r.userId} — ${r.type} (${STATUS_LABEL[r.status]})` });
      }
    }
    return out;
  }, [holidays, companyHolidays, allRequests, selectedUserIds, userMap, colorFor]);

  const renderOverlayCell = useCallback((args: RenderCellArgs<CalEvt>) => {
    const holiday = args.holiday;
    const company = args.eventsOnDay.find((e) => e.kind === "company");
    const reqs = args.eventsOnDay.filter((e) => e.kind === "request");
    const byUser = new Map<number, CalEvt>();
    for (const e of reqs) byUser.set(e.request!.userId, e);
    const uniqueUsers = Array.from(byUser.values());
    return (
      // Non-counting day: weekend or public holiday (these never
       // consume allowance, regardless of whether a company holiday
       // also sits on the same day — so the amber tint AND the user
       // dots get dimmed too, matching the personal-calendar view).
      // A company-only day still counts as non-counting because the
       // company holiday itself charges the allowance once; the
       // per-user request day on top of it would double-charge.
      // (Implementation below.)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      <div className={cn(
        "flex flex-col h-full",
        company && (args.isWeekend || !!holiday ? "bg-amber-500/5 rounded-sm -m-0.5 p-0.5" : "bg-amber-500/10 rounded-sm -m-0.5 p-0.5"),
      )}>
        <div className="flex items-start justify-between gap-1">
          <span className={cn("text-[10px] leading-none", args.isInCurrentMonth ? "text-gray-200" : "text-gray-500", holiday && "text-rose-300 font-medium", company && "text-amber-300 font-medium")}>
            {Number(args.date.slice(8, 10))}
          </span>
          {holiday && <span className="text-[8px] text-rose-300/80 truncate max-w-[60%]" title={holiday.holidayName}>{holiday.holidayName}</span>}
          {!holiday && company && (
            <span className={cn("text-[8px] truncate max-w-[60%]", args.isWeekend ? "text-amber-300/40" : "text-amber-300/90")} title={`Company holiday: ${company.companyHolidayName}`}>
              {company.companyHolidayName}
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-0.5 mt-0.5">
          {uniqueUsers.slice(0, 8).map((e, i) => {
            const noCharge = args.isWeekend || !!holiday || !!company;
            const baseOpacity = e.request!.status === "approved" ? 1 : 0.55;
            return (
              <span key={i} className="inline-block w-2 h-2 rounded-full"
                style={{ background: e.userColor, opacity: noCharge ? 0.2 : baseOpacity }}
                title={noCharge
                  ? `${userMap.get(e.request!.userId) ?? e.request!.userId} — ${e.request!.type} (does not count: ${holiday ? "public holiday" : args.isWeekend ? "weekend" : "company holiday"})`
                  : `${userMap.get(e.request!.userId) ?? e.request!.userId} — ${e.request!.type} (${STATUS_LABEL[e.request!.status]})`} />
            );
          })}
          {uniqueUsers.length > 8 && <span className="text-[8px] text-gray-400">+{uniqueUsers.length - 8}</span>}
        </div>
      </div>
    );
  }, [userMap]);

  const decide = useMutation({
    mutationFn: ({ id, approve, isCancellation, decisionNote }: { id: number; approve: boolean; isCancellation: boolean; decisionNote?: string | null }) =>
      apiRequest(
        "PATCH",
        isCancellation ? `/api/time-off/${id}/decide-cancellation` : `/api/time-off/${id}/decide`,
        { approve, decisionNote: decisionNote || null },
      ),
    onSuccess: (_data, vars) => {
      toast({ title: "Decision recorded" });
      queryClient.invalidateQueries({ queryKey: ["/api/time-off/all"] });
      queryClient.invalidateQueries({ queryKey: ["/api/time-off/usage"] });
      queryClient.invalidateQueries({ queryKey: ["/api/time-off/badge"] });
      setDecisionNotes((m) => { const n = { ...m }; delete n[vars.id]; return n; });
    },
    onError: () => toast({ title: "Decision failed", variant: "destructive" }),
  });

  const toggleUser = (id: number) => setSelectedUserIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const toggleStatus = (s: string) => setStatusFilter((cur) => cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]);

  return (
    <div className="space-y-4">
      {/* Filters */}
      <section className="rounded border border-gray-700 p-2 bg-gray-900/30 space-y-2">
        <div className="flex flex-wrap items-center gap-1">
          <span className="text-[11px] text-gray-400 mr-1">Users:</span>
          <Button variant="ghost" size="sm" className="h-6 px-2 text-[11px]" onClick={() => setSelectedUserIds([])}>All</Button>
          {teamUsers.map((u) => {
            const active = selectedUserIds.includes(u.id);
            return (
              <button
                key={u.id}
                onClick={() => toggleUser(u.id)}
                className={cn(
                  "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] border",
                  active ? "border-blue-500 bg-blue-900/30 text-blue-100" : "border-gray-700 bg-gray-800 text-gray-300 hover:bg-gray-700",
                )}
                title={u.username}
              >
                <span className="inline-block w-2 h-2 rounded-full" style={{ background: colorFor(u.id) }} />
                {u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.username}
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center gap-1">
          <span className="text-[11px] text-gray-400 mr-1">Statuses:</span>
          {STATUS_OPTIONS.map((s) => {
            const active = statusFilter.includes(s);
            return (
              <button
                key={s}
                onClick={() => toggleStatus(s)}
                className={cn(
                  "rounded px-1.5 py-0.5 text-[11px] border",
                  active ? "border-blue-500 bg-blue-900/30 text-blue-100" : "border-gray-700 bg-gray-800 text-gray-300 hover:bg-gray-700",
                )}
              >
                {STATUS_LABEL[s]}
              </button>
            );
          })}
        </div>
      </section>

      {/* Company holidays — admin only management */}
      <section className="rounded border border-gray-700 p-2 bg-gray-900/30 space-y-2">
        <div className="flex items-center gap-2">
          <Building2 className="w-3.5 h-3.5 text-emerald-300" />
          <h3 className="text-xs font-medium text-gray-300">Company holidays {year}</h3>
          <span className="text-[11px] text-gray-500">Apply to every active user; consume each user's allowance; no approval / notifications.</span>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div className="flex flex-col">
            <Label className="text-[10px] text-gray-400">Start</Label>
            <Input type="date" value={newCompanyStart} onChange={(e) => {
              setNewCompanyStart(e.target.value);
              if (!newCompanyEnd || newCompanyEnd < e.target.value) setNewCompanyEnd(e.target.value);
            }} className="h-7 w-36 bg-gray-800 border-gray-700 text-xs" />
          </div>
          <div className="flex flex-col">
            <Label className="text-[10px] text-gray-400">End</Label>
            <Input type="date" value={newCompanyEnd} min={newCompanyStart || undefined}
              onChange={(e) => setNewCompanyEnd(e.target.value)}
              className="h-7 w-36 bg-gray-800 border-gray-700 text-xs" />
          </div>
          <div className="flex flex-col flex-1 min-w-[160px]">
            <Label className="text-[10px] text-gray-400">Name</Label>
            <Input value={newCompanyName} onChange={(e) => setNewCompanyName(e.target.value.slice(0, 120))}
              placeholder="e.g. Betriebsferien"
              className="h-7 bg-gray-800 border-gray-700 text-xs" />
          </div>
          <Button size="sm" className="h-7"
            disabled={!newCompanyStart || !newCompanyName.trim() || addCompanyHoliday.isPending}
            onClick={() => addCompanyHoliday.mutate()}>
            <Plus className="w-3 h-3 mr-1" />Add
          </Button>
        </div>
        {companyHolidays.length > 0 && (
          <div className="rounded border border-gray-800 divide-y divide-gray-800">
            {companyHolidays.map((h) => {
              const days = expandRange(h.startDate, h.endDate);
              const holidaySet = new Set(holidays.map((x) => x.date));
              const businessDays = days.filter((d) => !isWeekendYmd(d) && !holidaySet.has(d)).length;
              return (
                <div key={h.id} className="flex items-center gap-2 px-2 py-1 text-xs">
                  <span className="text-gray-200 shrink-0 whitespace-nowrap">
                    {h.startDate === h.endDate ? h.startDate : `${h.startDate} → ${h.endDate}`}
                  </span>
                  <Badge variant="outline" className="px-1 py-0 h-4 shrink-0 border-emerald-600/70 text-emerald-200">≈{businessDays}d</Badge>
                  <span className="text-gray-300 truncate min-w-0">{h.name}</span>
                  <Button variant="ghost" size="sm" className="ml-auto shrink-0 h-6 px-2 text-[11px] text-rose-300 hover:text-rose-200"
                    onClick={() => deleteCompanyHoliday.mutate(h.id)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Overlay calendar */}
      <section>
        <h3 className="text-xs font-medium text-gray-300 mb-1">Team overlay</h3>
        <MonthCalendar
          year={year} month={month}
          events={overlayEvents}
          onMonthChange={onMonthChange}
          renderCell={renderOverlayCell as any}
          readOnly
        />
        <p className="text-[11px] text-gray-400 mt-1">Each dot is one user (deterministic colour). Faded dots are pending / cancellation requests.</p>
      </section>

      {/* Pending queue with decision notes */}
      <section>
        <h3 className="text-xs font-medium text-gray-300 mb-1">Pending queue ({pendingQueue.length})</h3>
        <div className="rounded border border-gray-700 divide-y divide-gray-800">
          {pendingQueue.length === 0 && <div className="p-3 text-xs text-gray-500">Nothing pending.</div>}
          {pendingQueue.map((r) => {
            const isCancel = r.status === "cancellation_pending";
            const note = decisionNotes[r.id] ?? "";
            return (
              <div key={r.id} className="px-2 py-1.5 text-xs space-y-1">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ background: colorFor(r.userId) }} />
                  <span className="text-gray-200 w-[140px] shrink-0 truncate">{userMap.get(r.userId) ?? `User #${r.userId}`}</span>
                  <Badge className={cn("px-1 py-0 h-4 shrink-0", TYPE_COLOR[r.type])}>{r.type}</Badge>
                  <span className="text-gray-200 shrink-0 whitespace-nowrap">{r.startDate} → {r.endDate}</span>
                  <Badge variant="outline" className="px-1 py-0 h-4 shrink-0 border-gray-600 text-gray-300">{STATUS_LABEL[r.status]}</Badge>
                  {r.reason && <ReasonChip reason={r.reason} />}
                  <div className="ml-auto shrink-0 flex items-center gap-1">
                    <Button variant="ghost" size="sm" className="h-6 px-2 text-[11px] text-emerald-300 hover:text-emerald-200"
                      onClick={() => decide.mutate({ id: r.id, approve: true, isCancellation: isCancel, decisionNote: note })}>
                      <Check className="w-3 h-3 mr-1" />Approve
                    </Button>
                    <Button variant="ghost" size="sm" className="h-6 px-2 text-[11px] text-rose-300 hover:text-rose-200"
                      onClick={() => decide.mutate({ id: r.id, approve: false, isCancellation: isCancel, decisionNote: note })}>
                      <X className="w-3 h-3 mr-1" />Reject
                    </Button>
                  </div>
                </div>
                <Input
                  value={note}
                  placeholder="Decision note (optional)"
                  onChange={(e) => setDecisionNotes((m) => ({ ...m, [r.id]: e.target.value }))}
                  className="h-7 bg-gray-800 border-gray-700 text-[11px]"
                />
              </div>
            );
          })}
        </div>
      </section>

      {/* Yearly usage */}
      <section>
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-xs font-medium text-gray-300">Yearly usage</h3>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" className="h-6 px-2" onClick={() => setYear(year - 1)}>−</Button>
            <span className="text-xs text-gray-200">{year}</span>
            <Button variant="ghost" size="sm" className="h-6 px-2" onClick={() => setYear(year + 1)}>+</Button>
          </div>
        </div>
        {(!usage || usage.rows.length === 0) ? (
          <div className="rounded border border-gray-700 p-3 text-center text-xs text-gray-500">No data.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {usage.rows.map((row) => {
              const allowance = row.allowance ?? 0;
              const totalApproved = row.approvedDays;
              const companyDays = row.companyDays ?? 0;
              // Server's approvedDays already includes the company-holiday
              // contribution; subtract to get the user's personal share.
              const personalApproved = Math.max(0, totalApproved - companyDays);
              const pending = row.pendingDays;
              const sickApproved = row.sickApprovedDays ?? 0;
              const sickPending = row.sickPendingDays ?? 0;
              const remaining = row.remaining ?? (allowance - totalApproved);
              const overBudget = remaining < 0;
              const projectedOver = allowance > 0 && (totalApproved + pending) > allowance;
              const personalPct = allowance > 0 ? Math.min(100, (personalApproved / allowance) * 100) : 0;
              const companyPct = allowance > 0 ? Math.min(100 - personalPct, (companyDays / allowance) * 100) : 0;
              const pendingPct = allowance > 0 ? Math.min(100 - personalPct - companyPct, (pending / allowance) * 100) : 0;
              const color = colorFor(row.userId);
              const name = row.firstName && row.lastName ? `${row.firstName} ${row.lastName}` : row.username;
              return (
                <div key={row.userId} className="rounded border border-gray-700 bg-gray-800/40 p-2 space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="inline-block w-2.5 h-2.5 rounded-full shrink-0" style={{ background: color }} />
                      <span className="text-xs text-gray-100 truncate font-medium">{name}</span>
                    </div>
                    <span className={cn(
                      "text-[11px] tabular-nums shrink-0 px-1.5 py-0.5 rounded",
                      overBudget ? "bg-rose-500/20 text-rose-300" : projectedOver ? "bg-amber-500/20 text-amber-300" : "bg-gray-700/60 text-gray-200",
                    )}>
                      {remaining} / {allowance || "—"} left
                    </span>
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-gray-700/60 overflow-hidden flex" title="Vacation usage (sick days excluded per §3 EFZG)">
                    <div className="h-full" style={{ width: `${personalPct}%`, background: color, opacity: 0.95 }} title={`Personal approved: ${personalApproved}`} />
                    <div
                      className="h-full"
                      style={{
                        width: `${companyPct}%`,
                        backgroundImage: `repeating-linear-gradient(45deg, ${color} 0 3px, rgba(0,0,0,0.35) 3px 6px)`,
                        opacity: 0.85,
                      }}
                      title={`Company holidays: ${companyDays}`}
                    />
                    <div className="h-full" style={{ width: `${pendingPct}%`, background: color, opacity: 0.4 }} title={`Pending: ${pending}`} />
                  </div>
                  <div className="flex items-center justify-between text-[10px] text-gray-400 tabular-nums">
                    <span><span className="text-gray-200">{personalApproved}</span> approved</span>
                    {companyDays > 0 && (
                      <span title="Company holidays charged to allowance">
                        <span className="text-gray-200">+{companyDays}</span> company
                      </span>
                    )}
                    <span><span className="text-gray-200">{pending}</span> pending</span>
                    <span><span className="text-gray-200">{allowance || "—"}</span> allowance</span>
                  </div>
                  {(sickApproved > 0 || sickPending > 0) && (
                    <div className="flex items-center justify-between gap-1 pt-1 border-t border-gray-700/60 text-[10px]" title="Sick days do not consume vacation allowance">
                      <span className="inline-flex items-center gap-1 text-orange-300/90">
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-orange-400" />
                        Sick
                      </span>
                      <span className="text-gray-400 tabular-nums">
                        <span className="text-orange-200">{sickApproved}</span> approved
                        {sickPending > 0 && <> · <span className="text-orange-200/70">{sickPending}</span> pending</>}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
